/**
 * Market session state for the AlphaNQ NY-session product.
 *
 * Session windows (America/New_York):
 *   Morning   — 09:30 to 11:30
 *   Afternoon — 13:00 to 15:00
 *
 * Weekends and outside-window times are "closed" with a countdown to
 * the next session open (Mon 09:30 AM ET if it's currently the weekend).
 *
 * Returns:
 *   {
 *     status:    'live' | 'closed',
 *     session:   'Morning' | 'Afternoon' | null,        // current OR next
 *     reason:    'weekend' | 'pre-market' | 'between' | 'post-market' | null,
 *     countdown: '12h 34m' | null,                      // until next open
 *     label:     'Live now' | 'Markets closed — Saturday' | etc.,
 *     nextOpen:  Date | null,
 *   }
 */

const MORNING_START = 9 * 60 + 30;   // 9:30
const MORNING_END   = 11 * 60 + 30;  // 11:30
const AFTERNOON_START = 13 * 60;     // 13:00
const AFTERNOON_END   = 15 * 60;     // 15:00

function nowInET() {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
}

function fmtCountdown(mins) {
  if (mins <= 0) return null;
  const d = Math.floor(mins / (60 * 24));
  const h = Math.floor((mins % (60 * 24)) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function minutesUntil(targetEt, fromEt) {
  return Math.max(0, Math.round((targetEt - fromEt) / 60000));
}

function nextSessionOpen(et) {
  const day = et.getDay();      // 0 Sun, 6 Sat
  const mins = et.getHours() * 60 + et.getMinutes();

  // Build candidate opens for today and the next 7 days
  for (let offset = 0; offset < 8; offset++) {
    const candidate = new Date(et);
    candidate.setDate(et.getDate() + offset);
    const cDay = candidate.getDay();
    if (cDay === 0 || cDay === 6) continue; // skip weekends

    // Try morning open
    const morning = new Date(candidate);
    morning.setHours(9, 30, 0, 0);
    if (offset === 0 && mins < MORNING_START) {
      return { date: morning, session: 'Morning' };
    }
    if (offset === 0 && mins < AFTERNOON_START && mins >= MORNING_END) {
      const afternoon = new Date(candidate);
      afternoon.setHours(13, 0, 0, 0);
      return { date: afternoon, session: 'Afternoon' };
    }
    if (offset > 0) {
      return { date: morning, session: 'Morning' };
    }
  }
  return null;
}

export function getMarketStatus() {
  const et = nowInET();
  const day = et.getDay();
  const mins = et.getHours() * 60 + et.getMinutes();
  const isWeekend = day === 0 || day === 6;

  // Live windows (weekdays only)
  if (!isWeekend) {
    if (mins >= MORNING_START && mins < MORNING_END) {
      return {
        status: 'live',
        session: 'Morning',
        reason: null,
        countdown: null,
        label: 'Live now',
        nextOpen: null,
      };
    }
    if (mins >= AFTERNOON_START && mins < AFTERNOON_END) {
      return {
        status: 'live',
        session: 'Afternoon',
        reason: null,
        countdown: null,
        label: 'Live now',
        nextOpen: null,
      };
    }
  }

  // Closed — figure out next open + a friendly reason
  const next = nextSessionOpen(et);
  const countdown = next ? fmtCountdown(minutesUntil(next.date, et)) : null;

  let reason = 'between';
  let label = 'Markets closed';
  if (isWeekend) {
    reason = 'weekend';
    label = day === 6 ? 'Markets closed — Saturday' : 'Markets closed — Sunday';
  } else if (mins < MORNING_START) {
    reason = 'pre-market';
    label = 'Pre-market — opens 9:30 AM ET';
  } else if (mins >= AFTERNOON_END) {
    reason = 'post-market';
    label = 'Markets closed for the day';
  } else {
    reason = 'between';
    label = 'On break — afternoon session at 1:00 PM ET';
  }

  return {
    status: 'closed',
    session: next ? next.session : null,
    reason,
    countdown,
    label,
    nextOpen: next ? next.date : null,
  };
}
